"use client";
import Image from "@/helpers/Image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils";
gsap.registerPlugin(ScrollTrigger);

export interface TraderPersonCardProps {
  photo?: GROQImage;
  image?: GROQImage;
  name?: string;
  position?: string;
  tradersText?: PortableTextBlock[];
  tradersQuote?: PortableTextBlock[];
  isFirst?: boolean;
  className?: string;
}

/**
 * CardAnimationNew — scroll-driven stacking card animation.
 *
 * Behaviour:
 *  - At most 3 cards are visible at once.
 *  - The "active" card sits centred at full scale.
 *  - Up to 2 previous cards are stacked behind it (scaled down, dimmed).
 *  - Any card more than 2 positions behind the active one vanishes (opacity 0).
 *  - Section height is proportional to (N-1) transitions, NOT the sum of all
 *    card heights — the section stays compact even with many cards.
 */
export default function CardAnimationNew({
  cards,
  futures = false,
}: {
  cards: TraderPersonCardProps[];
  futures?: boolean;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [cardHeight, setCardHeight] = useState<number | null>(null);

  // How much scroll distance each card transition takes (in px).
  const SCROLL_PER_CARD = 800;
  // Scroll distance before the first transition starts (lets the first card breathe).
  const INITIAL_DELAY = 600;

  // Stacking visual constants
  const SCALE_STEP = 0.08;
  const Y_OFFSET_STEP = 40; // px offset upward per stack level
  const OPACITY_STACKED_1 = 1;
  const OPACITY_STACKED_2 = 1;
  const BRIGHTNESS_ACTIVE = 1; // full brightness for active card
  const BRIGHTNESS_STACKED_1 = 0.8; // first card behind
  const BRIGHTNESS_STACKED_2 = 0.6; // second card behind

  const totalCards = cards?.length ?? 0;
  const totalTransitions = Math.max(0, totalCards - 1);
  const totalScroll = INITIAL_DELAY + totalTransitions * SCROLL_PER_CARD;

  // Set the .container height so the parent sticky has room to scroll
  const setContainerHeight = useCallback(() => {
    if (!sectionRef.current) return;
    // Use the normalised card height if available, otherwise measure the first card
    const measuredCardHeight =
      cardHeight ?? cardRefs.current[0]?.getBoundingClientRect().height ?? 0;

    const container = sectionRef.current.closest(".container");
    if (container instanceof HTMLElement) {
      const titleEl = container.querySelector(
        "div[data-cards-title]",
      ) as HTMLElement | null;
      const titleHeight = titleEl?.getBoundingClientRect().height ?? 0;
      container.style.height = `${totalScroll + titleHeight + measuredCardHeight + 200}px`;
    }
  }, [totalScroll, cardHeight]);

  // Measure the tallest card and normalise all cards to that height
  const measureMaxCardHeight = useCallback(() => {
    let max = 0;
    cardRefs.current.forEach((card) => {
      if (!card) return;
      // Temporarily clear any forced height so we measure natural content height
      card.style.height = "auto";
      const h = card.getBoundingClientRect().height;
      if (h > max) max = h;
    });
    if (max > 0) setCardHeight(max);
  }, []);

  // Measure & set container height on mount and resize
  useEffect(() => {
    measureMaxCardHeight();
    setContainerHeight();
    const onResize = () => {
      measureMaxCardHeight();
      setContainerHeight();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setContainerHeight, measureMaxCardHeight]);

  // Main GSAP animation
  useEffect(() => {
    if (!sectionRef.current || totalCards <= 1) return;

    // Small delay to ensure DOM is fully laid out
    const rafId = requestAnimationFrame(() => {
      setContainerHeight();
    });

    const ctx = gsap.context(() => {
      const wrapper =
        sectionRef.current?.closest("section") ?? sectionRef.current;
      if (!wrapper) return;

      // Set initial states: first card visible, rest hidden below
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        if (i === 0) {
          gsap.set(card, {
            opacity: 1,
            scale: 1,
            y: 0,
            yPercent: 0,
            zIndex: 10,
            filter: `brightness(${BRIGHTNESS_ACTIVE})`,
          });
        } else {
          gsap.set(card, {
            opacity: 0,
            scale: 1,
            y: 0,
            yPercent: 120,
            zIndex: 10,
          });
        }
      });

      // For each transition (card i → card i+1), create a ScrollTrigger
      for (let t = 0; t < totalTransitions; t++) {
        const transitionStart = INITIAL_DELAY + t * SCROLL_PER_CARD;
        const transitionEnd = transitionStart + SCROLL_PER_CARD;

        const incomingCard = cardRefs.current[t + 1];
        const outgoingCard = cardRefs.current[t];
        const deepStackCard = t > 0 ? cardRefs.current[t - 1] : null;
        const vanishingCard = t > 1 ? cardRefs.current[t - 2] : null;

        if (!incomingCard || !outgoingCard) continue;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: `top+=${transitionStart} center-=10%`,
            end: `top+=${transitionEnd} center-=10%`,
            scrub: 0.5,
            markers: false,
          },
        });

        // Incoming card: slide up from below
        tl.fromTo(
          incomingCard,
          {
            yPercent: 120,
            scale: 1,
            zIndex: 10,
            filter: `brightness(${BRIGHTNESS_ACTIVE})`,
          },
          {
            yPercent: 0,
            scale: 1,
            zIndex: 10,
            filter: `brightness(${BRIGHTNESS_ACTIVE})`,
            duration: 1,
          },
          0,
        );

        // Incoming card: fade in early (full opacity at 30% of transition)
        tl.fromTo(
          incomingCard,
          { opacity: 0 },
          { opacity: 1, duration: 0.3 },
          0,
        );

        // Outgoing card (was active): scale down, dim, move to stack level 1
        tl.to(
          outgoingCard,
          {
            scale: 1 - SCALE_STEP,
            opacity: OPACITY_STACKED_1,
            y: -Y_OFFSET_STEP,
            zIndex: 5,
            filter: `brightness(${BRIGHTNESS_STACKED_1})`,
            duration: 1,
          },
          0,
        );

        // Deep stack card: more scaled down, dimmer, stack level 2
        if (deepStackCard) {
          tl.to(
            deepStackCard,
            {
              scale: 1 - SCALE_STEP * 2,
              opacity: OPACITY_STACKED_2,
              y: -Y_OFFSET_STEP * 2,
              zIndex: 2,
              filter: `brightness(${BRIGHTNESS_STACKED_2})`,
              duration: 1,
            },
            0,
          );
        }

        // Vanishing card: fade out beyond visibility
        if (vanishingCard) {
          tl.to(
            vanishingCard,
            {
              scale: 1 - SCALE_STEP * 3,
              opacity: 0,
              y: -Y_OFFSET_STEP * 3,
              zIndex: 0,
              duration: 1,
            },
            0,
          );
        }
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      ctx.revert();
    };
  }, [totalCards, totalTransitions, setContainerHeight]);

  return (
    <div ref={sectionRef} className="relative z-[15]">
      <div className="relative">
        {cards?.map((item, index) => (
          <div
            key={item.name ?? `card-${index}`}
            ref={(el) => {
              cardRefs.current[index] = el;
            }}
            className="w-[min(112.625rem,100%)] mx-auto flex flex-col justify-end"
            style={{
              position: index === 0 ? "relative" : "absolute",
              top: 0,
              left: 0,
              right: 0,
              willChange: "transform, opacity, filter",
              ...(cardHeight ? { height: cardHeight } : {}),
            }}
          >
            <div
              className={cn(
                "flex relative flex-col items-center w-full lg:flex-row lg:items-end rounded-[2rem] overflow-clip backdrop-blur-[2rem]",
                futures ? "bg-[#DDDFEC]" : "bg-[#DEE4DB]",
              )}
            >
              <div className="relative flex-shrink-0 w-full overflow-clip max-w-[46rem] max-h-[50rem] lg:-mt-[2.5rem] lg:rounded-bl-[2rem]">
                {item.photo && (
                  <Image
                    className={cn(
                      "object-cover",
                      index === 0
                        ? "!w-full lg:!w-[106%] lg:max-w-[106%]"
                        : "w-[112%] max-w-[112%] lg:!w-[106%] lg:max-w-[106%]",
                      index === 5 || index === 6
                        ? "translate-x-0"
                        : "translate-x-4",
                      "lg:-translate-x-1",
                    )}
                    src={item.photo}
                    alt="Photo"
                  />
                )}

                <div className="lg:hidden absolute left-1/2 -translate-x-1/2 -bottom-px w-full h-[25rem] bg-gradient-to-t from-[#DEE4DB] from-[10%]" />
              </div>

              <div className="flex relative flex-col items-start pb-[3rem] px-[3rem] sm:pb-[4rem] sm:px-[4rem] lg:py-[4rem] lg:pl-[2.5rem] lg:pr-[4.5rem]">
                <div className="flex items-start lg:items-center gap-[1.75rem]">
                  {item.name !== "Dan Matern" && item.image && (
                    <Image className="!w-[4.375rem]" src={item.image} alt="" />
                  )}

                  <div className="flex flex-col items-start">
                    {item.name && (
                      <p className="font-medium tracking-tight leading-none mb-[.5rem] text-[2.75rem] text-[#07171B]">
                        {item.name}
                      </p>
                    )}

                    {item.position && (
                      <p className="mb-0 font-medium tracking-tight text-[2rem] text-[#07171B]/50 leading-[1.2]">
                        {item.position}
                      </p>
                    )}
                  </div>
                </div>

                <hr className="w-full border-t my-[2.25rem] [border-image:repeating-linear-gradient(to_right,#07171B_0,#07171B_7px,transparent_7px,transparent_14px)_1]" />

                {item.tradersText && (
                  <div className="richtext richtext-start prose-p:lg:text-[1.9rem] prose-p:text-[#07171B]/80">
                    <PortableText
                      value={item.tradersText}
                      components={richTextComponent}
                    />
                  </div>
                )}

                <hr className="w-full border-t my-[2.25rem] [border-image:repeating-linear-gradient(to_right,#07171B_0,#07171B_7px,transparent_7px,transparent_14px)_1]" />

                {item.tradersQuote && (
                  <div className="richtext richtext-start prose-p:font-medium prose-p:text-[2rem] prose-p:text-[#07171B]/70 lg:prose-p:text-[2.25rem]">
                    <PortableText
                      value={item.tradersQuote}
                      components={richTextComponent}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
